// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Setup Screen (2-step wizard)
// Step 1: POS selection (auto-detected or manual)
// Step 2: Confirmation
// ---------------------------------------------------------------------------

import { useState, useEffect, useRef } from 'react';
import { useConfigStore } from '@/store/config-store';
import { useAuthStore } from '@/store/auth-store';
import { apiClient } from '@/services/api-client';

interface PosOption {
  id: number;
  name: string;
  company_id: number;
  zone?: string;
  status?: string;
}

export default function SetupScreen() {
  const setPosConfig = useConfigStore((s) => s.setPosConfig);
  const deviceId = useConfigStore((s) => s.deviceId);
  const company = useAuthStore((s) => s.company);

  // Wizard state
  const [step, setStep] = useState(1);

  // Step 1: POS selection
  const [posList, setPosList] = useState<PosOption[]>([]);
  const [selectedPos, setSelectedPos] = useState<PosOption | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [manualPosId, setManualPosId] = useState('');
  const [manualPosName, setManualPosName] = useState('');

  const loadedRef = useRef(false);

  // Load POS list on mount
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const list = await apiClient.getPosList();
        if (list.length > 0) {
          setPosList(list);
          setManualMode(false);
        } else {
          setManualMode(true);
        }
      } catch (err) {
        setLoadError((err as Error).message);
        setManualMode(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const selectPos = (pos: PosOption) => {
    setSelectedPos(pos);
    setStep(2);
  };

  const validateManualPos = () => {
    const pId = parseInt(manualPosId, 10);
    if (!pId) return;

    setSelectedPos({
      id: pId,
      name: manualPosName || `Caisse #${pId}`,
      company_id: company?.id ?? 0,
    });
    setStep(2);
  };

  // Step 2: Confirm
  const confirmSetup = () => {
    if (!selectedPos) return;

    setPosConfig(
      selectedPos.company_id || company?.id || 0,
      selectedPos.id,
      selectedPos.name,
    );
  };

  // Step indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-3 mb-8">
      {[1, 2].map((s, idx) => (
        <div key={s} className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
              step > s
                ? 'bg-success text-white'
                : step === s
                  ? 'bg-accent text-white'
                  : 'bg-surface-card text-gray-400'
            }`}
          >
            {step > s ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              s
            )}
          </div>
          {idx < 1 && (
            <div className={`w-8 h-0.5 ${step > s ? 'bg-success' : 'bg-surface-card'}`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="h-full flex items-center justify-center bg-surface p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Configuration Caisse</h1>
          <p className="text-gray-400 text-sm">
            {company?.name
              ? `${company.name} -- Selectionnez votre poste`
              : 'Selectionnez votre poste de caisse'}
          </p>
        </div>

        <StepIndicator />

        {/* Step 1: POS Selection */}
        {step === 1 && (
          <div className="card p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-white mb-2">Selectionnez votre caisse</h2>

            {loading && (
              <div className="text-center py-8 text-gray-500 text-sm">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin mx-auto mb-2 text-accent">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Chargement des caisses...
              </div>
            )}

            {!loading && loadError && (
              <div className="text-center py-4">
                <p className="text-danger text-sm mb-2">{loadError}</p>
                <p className="text-gray-500 text-xs">Saisie manuelle disponible ci-dessous</p>
              </div>
            )}

            {!loading && !manualMode && posList.length > 0 && (
              <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
                {posList.map((pos) => (
                  <button
                    key={pos.id}
                    type="button"
                    onClick={() => selectPos(pos)}
                    className="w-full flex items-center gap-3 bg-surface-card rounded-xl p-4 text-left hover:bg-surface-hover transition-colors active:scale-[0.98]"
                  >
                    <div className="w-10 h-10 bg-accent/20 text-accent rounded-lg flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{pos.name || `Caisse #${pos.id}`}</p>
                      {pos.zone && <p className="text-gray-400 text-xs">{pos.zone}</p>}
                    </div>
                    {pos.status === 'active' && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-success/20 text-success">
                        Active
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {!loading && manualMode && (
              <div className="space-y-3 mb-4">
                <p className="text-gray-500 text-xs mb-2">
                  Saisie manuelle (demandez ces infos a votre manager)
                </p>
                <div>
                  <label htmlFor="manual-pos" className="block text-xs text-gray-300 mb-1">
                    ID Caisse (POS)
                  </label>
                  <input
                    id="manual-pos"
                    type="number"
                    value={manualPosId}
                    onChange={(e) => setManualPosId(e.target.value)}
                    placeholder="Ex: 5"
                    inputMode="numeric"
                    className="w-full bg-surface-card text-white rounded-xl px-4 py-2.5 text-sm outline-none border border-white/10 focus:border-accent"
                  />
                </div>
                <div>
                  <label htmlFor="manual-pos-name" className="block text-xs text-gray-300 mb-1">
                    Nom de la caisse (optionnel)
                  </label>
                  <input
                    id="manual-pos-name"
                    type="text"
                    value={manualPosName}
                    onChange={(e) => setManualPosName(e.target.value)}
                    placeholder="Ex: Caisse Bar"
                    className="w-full bg-surface-card text-white rounded-xl px-4 py-2.5 text-sm outline-none border border-white/10 focus:border-accent"
                  />
                </div>
                <button
                  type="button"
                  onClick={validateManualPos}
                  className="btn-primary w-full py-2.5"
                >
                  Valider
                </button>
              </div>
            )}

            {!loading && !manualMode && posList.length > 0 && (
              <button
                type="button"
                onClick={() => setManualMode(true)}
                className="text-gray-400 hover:text-white text-xs text-center w-full mt-2"
              >
                Saisie manuelle
              </button>
            )}
          </div>
        )}

        {/* Step 2: Confirmation */}
        {step === 2 && selectedPos && (
          <div className="card p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-white mb-4">Confirmation</h2>

            <div className="space-y-3 mb-6">
              {[
                { label: 'Etablissement', value: company?.name || `Etablissement #${selectedPos.company_id}` },
                { label: 'Caisse', value: selectedPos.name || `Caisse #${selectedPos.id}` },
                { label: 'Device ID', value: deviceId },
              ].map((row) => (
                <div key={row.label} className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-gray-400 text-sm">{row.label}</span>
                  <span className="text-white text-sm font-medium truncate ml-4 max-w-[200px]">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="btn-secondary flex-1 py-2.5"
              >
                Modifier
              </button>
              <button
                type="button"
                onClick={confirmSetup}
                className="btn-primary flex-1 py-2.5 font-semibold"
              >
                Confirmer et demarrer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
